/* nimbus-motion-effects.js
 * Global effects that are not section-specific:
 *   1. Stat counters  — [data-motion-counter] and legacy .sb-stat__value
 *   2. Header scroll-hide  — hides on scroll-down, shows on scroll-up
 *   3. Watermark drift  — .sb-section-num gentle vertical drift
 *
 * Exposed on window.NimbusMotion.effects.
 * Registered into the engine's boot chain via NM.effects.init.
 */
(function () {
  'use strict';

  const NM = window.NimbusMotion = window.NimbusMotion || {};
  const MM = NM.MM;

  /* ─── 1. Stat counters ───────────────────────────────────────────── *
   * Selectors (both work):
   *   [data-motion-counter]            — new API
   *   .sb-stat__value                  — legacy (inside .nimbus-anim or not)
   *
   * Behaviour:
   *   • Fires once via ScrollTrigger.
   *   • Parses numeric value + non-digit suffix from textContent.
   *   • data-motion-counter-suffix="%" overrides auto-parsed suffix.
   *   • Tweens a proxy object so the update hook has a single point of truth.
   *   • Reduced motion: jumps to final value immediately.
   */
  function initCounters(root) {
    const scope = root === document ? document : root;
    const els   = [
      ...scope.querySelectorAll('[data-motion-counter]'),
      ...scope.querySelectorAll('.sb-stat__value')
    ];

    /* De-duplicate (an element might match both selectors) */
    const unique = [...new Set(els)];

    unique.forEach(el => {
      if (el._nimbusCounter) return;
      el._nimbusCounter = true;

      const raw    = el.textContent.trim();
      const suffix = el.dataset.motionCounterSuffix !== undefined
        ? el.dataset.motionCounterSuffix
        : raw.replace(/[\d,.\s]/g, '');
      const target = parseInt(raw.replace(/[^\d]/g, ''), 10);
      if (isNaN(target) || target <= 0) return;

      const reduced = window.matchMedia(MM.reduced).matches;

      if (reduced) {
        el.textContent = target.toLocaleString() + suffix;
        return;
      }

      const proxy = { n: 0 };

      ScrollTrigger.create({
        trigger: el,
        start: 'top 75%',
        once: true,
        onEnter() {
          gsap.to(proxy, {
            n: target,
            duration: 1.1,
            ease: 'power3.out',
            onUpdate() {
              el.textContent = Math.round(proxy.n).toLocaleString() + suffix;
            },
            onComplete() {
              /* Snap to exact value to avoid rounding drift */
              el.textContent = target.toLocaleString() + suffix;
            }
          });
        }
      });
    });
  }

  /* ─── 2. Header scroll-hide ──────────────────────────────────────── *
   * Hides the site header on scroll-down (past 80px threshold),
   * reveals it on scroll-up. Disabled inside the Theme Editor.
   *
   * Targets: .nimbus-site-header (primary) or header[role="banner"]
   */
  function initHeaderScrollHide() {
    if (window.Shopify && window.Shopify.designMode) return;

    const header = document.querySelector('.nimbus-site-header')
                || document.querySelector('header[role="banner"]')
                || document.querySelector('#shopify-section-header');
    if (!header) return;

    const reduced = window.matchMedia(MM.reduced).matches;
    if (reduced) return;

    /* Only on tablet + desktop — mobile header is usually short enough */
    const mm = gsap.matchMedia();

    mm.add(`${MM.isTablet}, ${MM.isDesktop}`, () => {
      let lastY = 0;
      let hidden = false;
      const THRESHOLD = 80;

      const headerH = () => header.getBoundingClientRect().height;

      ScrollTrigger.create({
        start: 0,
        end: 'max',
        onUpdate(self) {
          const y = self.scroll();
          if (y < THRESHOLD) {
            if (hidden) {
              gsap.to(header, { y: 0, duration: 0.4, ease: 'power2.out', overwrite: true });
              hidden = false;
            }
          } else if (self.direction === 1 && !hidden) {
            gsap.to(header, { y: -headerH(), duration: 0.4, ease: 'power2.in', overwrite: true });
            hidden = true;
          } else if (self.direction === -1 && hidden) {
            gsap.to(header, { y: 0, duration: 0.4, ease: 'power2.out', overwrite: true });
            hidden = false;
          }
          lastY = y;
        }
      });
    });
  }

  /* ─── 3. Section-number watermark drift ──────────────────────────── *
   * .sb-section-num elements drift gently upward as the section scrolls
   * by. Subtle — much slower than content parallax (strength 0.08).
   * Disabled on mobile and for reduced-motion users.
   */
  function initWatermarkDrift(root) {
    const scope = root === document ? document : root;
    const nums  = scope.querySelectorAll('.sb-section-num');
    if (!nums.length) return;

    const reduced = window.matchMedia(MM.reduced).matches;
    if (reduced) return;

    const mm = gsap.matchMedia();

    mm.add(`${MM.isTablet}, ${MM.isDesktop}`, () => {
      nums.forEach(num => {
        if (num._nimbusWatermark) return;
        num._nimbusWatermark = true;

        const section = num.closest('.nimbus-section-wrap') || num.parentElement;

        ScrollTrigger.create({
          trigger: section,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2,
          onUpdate(self) {
            const offset = (self.progress - 0.5) * section.offsetHeight * 0.08;
            gsap.set(num, { y: offset });
          }
        });
      });
    });
  }

  /* ─── Unified init ───────────────────────────────────────────────── */
  function init(root) {
    initCounters(root);
    initWatermarkDrift(root);

    /* Header is page-level — only init once on full-page boot */
    if (root === document) {
      initHeaderScrollHide();
    }
  }

  NM.effects = { init, initCounters, initHeaderScrollHide, initWatermarkDrift };
})();
